import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Label,
  TableCell,
  TableRow,
  Typography,
} from '@tempmee/react';
import {
  getMembershipStatusSeverity,
  useGetUserCycles,
  useUpdateUserCycleStatus,
} from '../../../../domain/Awards';
import useClampedPage from '../../../../hooks/useClampedPage';
import { DsPagination } from '../../../commonComponents/DsPagination';
import { MembershipStatusSelect } from '../../../commonComponents/MembershipStatusSelect';
import { TableCardFrame } from '../../../commonComponents/TableCardFrame';

const COLUMNS = ['Cycle', 'End Date', 'Contest Status'];

// A node, not a string: TableCard's default string treatment is 18px bold,
// which reads too heavy inside the profile tab — subtitle2 keeps it a clear
// notch above the table's column headers without competing with the page.
const CARD_TITLE = (
  <Typography variant="subtitle2" as="h2" bold className="truncate text-text-primary">
    Best in Dental
  </Typography>
);

/**
 * The profile Promotion tab's Best in Dental section (GOTU-4819 office /
 * GOTU-5817 professional — one component, the endpoint is user-id based):
 * every cycle, newest-created first, crossed with this user's status in it.
 *
 * The status control is the same one the cycle roster uses, so both screens
 * offer the same vocabulary and grey the same illegal transitions. It locks
 * once the cycle's end date has passed — the same gate the server enforces.
 * An edit here is a formal entry or removal: the mutation invalidates the
 * cycle's roster so the Cycle page's user list gains or loses this user.
 */
export const BestInDentalCycleHistory = ({ userId }) => {
  const [page, setPage] = useState(1);

  const {
    userCycles,
    pageSize,
    totalCount,
    isPending,
    isError,
  } = useGetUserCycles(userId, page);

  // A deleted cycle (or a stale total) can strand the page past the end of the
  // list; pull it back into range so the next fetch lands on data. A failed
  // fetch has no total (nullish → never clamp) — clamping on its 0 fallback
  // would silently reset the page and refetch over the error state.
  useClampedPage(page, isError ? null : totalCount, pageSize, setPage);

  // Rejections surface through the hook's default toast, which relays the
  // server's admin-facing message; per-row pending state rides along too.
  const updateStatus = useUpdateUserCycleStatus(userId);

  return (
    <TableCardFrame
      title={CARD_TITLE}
      // Forwarded through TableCard to the DS Card: without it the card is a
      // plain white div that disappears into the tab's white background.
      outline
      columns={COLUMNS}
      rowCount={userCycles.length}
      isPending={isPending}
      isError={isError}
      emptyMessage="No cycles have been created yet."
      errorMessage="Could not load Best in Dental cycles."
      footer={(
        <DsPagination
          page={page}
          pageSize={pageSize}
          totalCount={totalCount}
          onPageChange={setPage}
        />
      )}
    >
      {userCycles.map((row) => (
        <TableRow key={row.cycleId} hover>
          <TableCell>{row.cycleName}</TableCell>
          <TableCell>{row.endDateLabel}</TableCell>
          <TableCell>
            {row.isStatusEditable ? (
              <MembershipStatusSelect
                row={row}
                disabled={updateStatus.isRowPending(row.cycleId)}
                onChange={(status) => updateStatus.editStatus(row.cycleId, status)}
              />
            ) : (
              <Label severity={getMembershipStatusSeverity(row.status)} variant="standard">
                {row.statusLabel}
              </Label>
            )}
          </TableCell>
        </TableRow>
      ))}
    </TableCardFrame>
  );
};

BestInDentalCycleHistory.propTypes = {
  /** Monolith user id of the profile being viewed (office or professional). */
  userId: PropTypes.string.isRequired,
};
